import { authGet, authPost } from '../http_client.js';
import type { BoosterMutationResponse, LevelFailedResponse, LevelCompleteResponse, LevelDoubleRewardResponse, PlayerDto, PurchaseItem, PurchaseResponse, ReviveOfferResponse, RewardBoosterResponse, RewardBoosterType, RewardHeartResponse } from '../dto/player.dto.js';

export const APP_RUN_ID = globalThis.crypto?.randomUUID?.()
  ?? `run_${Date.now()}_${Math.random().toString( 36 ).slice( 2 )}`;

export async function reportLevelFailed( occurred_at?: string ): Promise<LevelFailedResponse>
{
  return authPost<LevelFailedResponse>( 'api/player/me/level-failed', occurred_at ? { occurred_at } : undefined );
}

export async function completeLevel( level: number, stars: number ): Promise<LevelCompleteResponse>
{
  return authPost<LevelCompleteResponse>( 'api/player/me/level-complete', { level, stars } );
}

export async function doubleLevelReward( level: number, stars: number ): Promise<LevelDoubleRewardResponse>
{
  return authPost<LevelDoubleRewardResponse>( 'api/player/me/level-double-reward', { level, stars } );
}

export async function purchaseItem(
  item: PurchaseItem,
  expected_offer?: { purchase_index?: number; price: number; qty: number }
): Promise<PurchaseResponse>
{
  return authPost<PurchaseResponse>( 'api/player/me/purchase', {
    item,
    ...( expected_offer ? {
      ...( expected_offer.purchase_index !== undefined
        ? { expected_purchase_index: expected_offer.purchase_index }
        : {} ),
      expected_price: expected_offer.price,
      expected_qty: expected_offer.qty,
    } : {} ),
  } );
}

export async function getReviveOffer(): Promise<ReviveOfferResponse>
{
  return authGet<ReviveOfferResponse>( 'api/player/me/revive-offer' );
}

export async function rewardHeart(): Promise<RewardHeartResponse>
{
  return authPost<RewardHeartResponse>( 'api/player/me/reward-heart' );
}

export async function rewardBooster(
  booster_type: RewardBoosterType,
  placement: string,
  ad_id: string
): Promise<RewardBoosterResponse>
{
  return authPost<RewardBoosterResponse>( 'api/player/me/booster/reward', {
    booster_type,
    placement,
    ad_id,
  } );
}

export async function consumeBooster(
  booster_type: RewardBoosterType,
  request_id: string
): Promise<BoosterMutationResponse>
{
  return authPost<BoosterMutationResponse>( 'api/player/me/booster/consume', {
    booster_type,
    request_id,
  } );
}

export async function refundBooster(
  booster_type: RewardBoosterType,
  request_id: string
): Promise<BoosterMutationResponse>
{
  return authPost<BoosterMutationResponse>( 'api/player/me/booster/refund', {
    booster_type,
    request_id,
  } );
}

export async function resetProgress(): Promise<{ player: PlayerDto }>
{
  return authPost<{ player: PlayerDto }>( 'api/player/me/reset-progress', { confirm: true } );
}

/**
 * Debug-console level jump. player_level is server-authoritative — it only
 * moves on level-complete — so writing the level into local storage alone is
 * undone by the next player sync; this is what makes the change stick.
 */
export async function debugSetLevel( level: number ): Promise<{ player: PlayerDto }>
{
  return authPost<{ player: PlayerDto }>( 'api/player/me/debug/set-level', { level } );
}

/** Debug console: forget this player's progress on one offer (chain cursor, purchase cycle). Non-prod servers only. */
export type SessionStartResponse = {
  ok: boolean;
  abandoned_attempt_settled: boolean;
  hearts: number;
  max_hearts: number;
  next_heart_at: string | null;
};

export async function reportSessionStart( settle_abandoned = false ): Promise<SessionStartResponse>
{
  return authPost<SessionStartResponse>( 'api/player/me/session-start', {
    settle_abandoned,
    app_run_id: APP_RUN_ID,
  } );
}

export async function reportAdImpression( ad_type: string, revenue?: number ): Promise<{ ok: boolean }>
{
  return authPost<{ ok: boolean }>( 'api/player/me/ad-impression', { type: ad_type, revenue } );
}

export type LevelStartResponse = {
  ok: boolean;
  attempt_number: number;
  attempt_mass_multiplier: number;
  attempt_mass: {
    multiplier: number;
    source: string;
    range?: { from: number; to: number };
  };
};

export async function reportLevelStart( level: number, app_run_id: string = APP_RUN_ID, occurred_at?: string ): Promise<LevelStartResponse>
{
  return authPost<LevelStartResponse>( 'api/player/me/level-start', {
    level,
    app_run_id,
    ...( occurred_at ? { occurred_at } : {} ),
  } );
}

export async function reportLevelActive( active: boolean ): Promise<{ ok: boolean }>
{
  return authPost<{ ok: boolean }>( 'api/player/me/level-active', { active } );
}

let rv_offer_reported = false;

export function reportRvOfferShown(): void
{
  if ( rv_offer_reported )
    return;

  rv_offer_reported = true;
  void authPost( 'api/player/me/rv-offer-shown' )
    .catch( e =>
    {
      rv_offer_reported = false;
      console.warn( '[Economy] rv-offer-shown failed', e );
    } );
}
